import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import {
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import ImpactSummaryEditComponent, {
  ImpactSummaryEditComponentProps,
} from './ImpactSummaryEditComponent';
import { ButtonProps } from 'shared/design-system/components/TrueImpactSimpleModal';
import { ImpactSummaryHandler } from './utils';

interface ImpactSummaryEditProps
  extends Omit<ImpactSummaryEditComponentProps, 'questionRef'> {
  open: boolean;
  handleCloseModal: () => unknown;
  handleUpdate: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorWithUpdate,
    itemKey: keyof IndicatorWithUpdate,
    answer: IndicatorWithUpdate[keyof IndicatorWithUpdate],
  ) => unknown;
  handleVerified: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorWithUpdate,
    itemKey: keyof IndicatorWithUpdate,
  ) => unknown;
}

const StyledDiv = styled.div``;

const ImpactSummaryEdit: React.FC<ImpactSummaryEditProps> = ({
  open,
  sectionKey,
  item,
  itemKey,
  reportId,
  indicators,
  handleCloseModal,
  handleUpdate,
  handleVerified,
}) => {
  const [errorText, setErrorText] = useState<string | undefined>();
  const ref = React.useRef<ImpactSummaryHandler>(null);

  const handleSave = useCallback(
    (
      indicator: IndicatorWithUpdate,
      itemKey: keyof IndicatorWithUpdate,
      answer: IndicatorWithUpdate[keyof IndicatorWithUpdate],
    ) => {
      (indicator[itemKey] as typeof answer) = answer;
    },
    [reportId],
  );

  const handleDone = useCallback(() => {
    if (!ref.current) return;
    const { answer, errorText } = ref.current.answer?.();
    if (errorText) {
      setErrorText(errorText);
      return;
    }
    setErrorText(undefined);
    handleSave(item, 'updatedText', answer);
    handleUpdate(sectionKey, item, 'updatedText', answer);
    handleVerified(sectionKey, item, itemKey);
    handleCloseModal();
  }, [
    sectionKey,
    item,
    itemKey,
    handleUpdate,
    setErrorText,
    handleSave,
    handleCloseModal,
    handleVerified,
  ]);

  const next: ButtonProps = {
    text: 'Save & Continue',
    onClick: handleDone,
    isLoading: false,
  };

  return (
    <TrueImpactModal
      open={open}
      maxWidth="1100px"
      errorText={errorText}
      onClose={handleCloseModal}
      next={next}
      previous={undefined}
    >
      <StyledDiv>
        <ImpactSummaryEditComponent
          ref={ref}
          item={item}
          itemKey={itemKey}
          reportId={reportId}
          indicators={indicators}
          sectionKey={sectionKey}
        />
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ImpactSummaryEdit;
